/* eslint-disable */
import React from 'react';
import { I18N_LOCALES } from '../../Constants/I18n/I18n.constants';
import throwI18nInvalidHandlerTypeError from '../../Errors/I18n/I18nInvalidHandlerType.error';
import throwI18nInvalidTranslationTypeError from '../../Errors/I18n/I18nInvalidTranslationType.error';
import throwModelCouldNotUnsubscribeError from '../../Errors/Model/ModelCouldNotUnsubscribe.error';
import i18nFindLocaleByLang from '../../Support/I18n/FindLocaleByLang/I18nFindLocaleByLang.support';
import i18nValidateKey from '../../Support/I18n/ValidateKey/I18nValidateKey.support';
import isFunction from '../../Support/Is/Function/IsFunction';
import isNumber from '../../Support/Is/Number/IsNumber.support';
import isString from '../../Support/Is/String/IsString.support';
import './I18n.service.i18n';

const doc = document;

/**
 * I18nService
 * @class I18nService
 */
class I18nService {
  /**
   * The current `locale`
   * @type {I18nEntityLocale}
   * @memberof I18nService
   */
  _locale;

  /**
   * The list of handlers that will respond to locale changes
   * @type {Array<I18nServiceLocaleChangedHandlerInvoker>}
   * @memberof I18nService
   */
  _localeChangedHandlers = [];

  /**
   * All of the defined `locales`
   * @type {Array<I18nEntityLocale>}
   * @memberof I18nService
   */
  _locales = I18N_LOCALES;

  /**
   * The `StorageService`
   * @type {StorageService}
   * @memberof I18nService
   */
  storageService;

  /**
   * Creates an instance of `I18nService`
   * @param {StorageServiceCreateDto} dto The params required to create an instance of `I18nService`
   * @memberof I18nService
   */
  constructor({ storageService }) {
    this.storageService = storageService;
  }

  /**
   * Create a handler to respond to locale changes
   * @param {string} key The `key` to get the translation for
   * @param {any} data This is any data that may be required to aid in the translation
   * @param {I18nServiceLocaleChangedHandler} handler A method to be continously invoked upon locale change
   * @returns {I18nServiceLocaleChangedHandlerInvoker} A Function that passes the translated `text` to a given `handler
   * @memberof I18nService
   */
  _createLocaleChangedHandler = (key, data, handler) => {
    return async () => {
      const i18nKey = this.getKey(key);
      const { lang } = this.getLocale();
      const value = await this.storageService.getItem(i18nKey);
      if (value === null) {
        handler(null, { key, lang, translation: null });
        return;
      }
      const translation = await this._validateTranslation(key, value, data);
      handler(null, { key, lang, translation });
    };
  };

  /**
   * Create an unsubscribe handler that can be used to stop listening to the locale changes
   * @param {I18nServiceLocaleChangedHandler} localeChangedHandler The handler to unsubscribe
   * @returns {I18nServiceLocaleChangedUnsubscribe} A Function that will perform the unsubscribe
   * @memberof I18nService
   */
  _createLocaleChangedUnsubscribe = (localeChangedHandler) => {
    return () => {
      const index = this._localeChangedHandlers.findIndex((handler) => handler === localeChangedHandler);
      if (index === -1) {
        return throwModelCouldNotUnsubscribeError(`localeChangedHandler`);
      }
      this._localeChangedHandlers.splice(index, 1);
    };
  };

  /**
   * Queue up a `handler` to be executed. This is done to kick off the initial translation. The `setTimeout()` is used to handle cases where a Component has
   * already unmounted before the handler is ready to be called. This is the reason for the additional `includes()` check.
   * @param {I18nServiceLocaleChangedHandler} localeChangedHandler The handler to call
   * @returns {void} void
   * @memberof I18nService
   */
  _queueLocaleChangedHandlers = (localeChangedHandler) => {
    setTimeout(() => {
      if (this._localeChangedHandlers.includes(localeChangedHandler)) {
        localeChangedHandler();
      }
    }, 1);
  };

  /**
   * Here we will validate that the `translation` that we received is of the right type. And in the event that it is a Function, we will recursivelly call this
   * validation method until we settle on a Number or String. This method also supports retrieving the `text` asychnously.
   * @param {string} key The `key` to use to find the `translation`. Here, we only use this to construct meaningful Error messages
   * @param {I18nServiceTranslationFunction | number | string} value The translation to be validated
   * @param {any} data This is any data that may be required to aid in the translation
   * @returns {Promise<string>} The translated `text`
   * @memberof I18nService
   */
  _validateTranslation = async (key, value, data) => {
    if (React.isValidElement(value)) {
      return value;
    }
    if (isNumber(value)) {
      return String(value);
    }
    if (isString(value)) {
      return value;
    }
    if (isFunction(value)) {
      /** @type {I18nServiceTranslationFunction | number | string} */
      const newValue = await Promise.resolve().then(() => value(data));
      return this._validateTranslation(key, newValue, data);
    }
    return throwI18nInvalidTranslationTypeError(key, value);
  };

  /**
   * Get the key to use in the `I18nService`
   * @param {string} key The `key` to use
   * @returns {string} The `key` to use
   * @memberof I18nService
   */
  getKey = (key) => {
    i18nValidateKey(key);
    const { lang } = this.getLocale();
    const result = `${lang}.${key}`;
    return result;
  };

  /**
   * Get the current `label`
   * @returns {I18nEntityLocale} The current `label`
   * @memberof I18nService
   */
  getLocale = () => {
    return this._locale;
  };

  /**
   * Get the `locales`
   * @returns {string} The current `locales`
   * @memberof I18nService
   */
  getLocales = () => {
    return this._locales;
  };

  /**
   * Get a translation for a given `key`
   * @param {string} key The `key` to get the translation for
   * @param {any} data This is any data that may be required to aid in the translation
   * @param {I18nServiceLocaleChangedHandler} handler A method to be continously invoked upon locale change
   * @returns {I18nServiceLocaleChangedUnsubscribe} A Function that can be used to unsubscribe from listening to locale changes
   * @memberof I18nService
   */
  observeTranslation = (key, data, handler) => {
    /* eslint-disable no-param-reassign */ // Enable currying of arguments
    if (isFunction(data)) {
      handler = data;
      data = {};
    }
    if (data == null) {
      data = {};
    }
    /* eslint-enable no-param-reassign */
    if (!isFunction(handler)) {
      return throwI18nInvalidHandlerTypeError(handler);
    }
    const localeChangedHandler = this._createLocaleChangedHandler(key, data, handler);
    this._localeChangedHandlers.push(localeChangedHandler);
    this._queueLocaleChangedHandlers(localeChangedHandler);
    const unsubscribe = this._createLocaleChangedUnsubscribe(localeChangedHandler);
    return unsubscribe;
  };

  /**
   * Set the current `locale` using the provided `lang`
   * @param {I18nServiceSetLocaleDto} dto The required params to set the `locale`
   * @returns {void} void
   * @memberof I18nService
   */
  setLocale = ({ lang }) => {
    const locale = i18nFindLocaleByLang(lang, this._locales);
    this._locale = locale;
    doc.documentElement.lang = this._locale.lang;
    doc.documentElement.dir = this._locale.direction;
    doc.body.dir = this._locale.direction;
    this._localeChangedHandlers.forEach((localeChangedHandler) => {
      localeChangedHandler(locale);
    });
  };

  /**
   * Get a translation for a given `key`
   * @param {string} key The `key` to get the translation for
   * @param {any} data This is any data that may be required to aid in the translation
   * @returns {Promise<string>} The translated `text`
   * @memberof I18nService
   */
  getTranslation = async (key, data) => {
    const i18nKey = this.getKey(key);
    const value = await this.storageService.getItem(i18nKey);
    if (value === null) {
      return null;
    }
    const translation = await this._validateTranslation(key, value, data);
    return translation;
  };
}

export default I18nService;
